import { type ReactNode, useEffect, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * The one modal dialog in the product.
 *
 * Everything a dialog has to get right is here once rather than at every call
 * site: the role and accessible name, focus moved inside on open and returned
 * to the control that opened it on close, Escape, a Tab loop that cannot leave,
 * and a background that is inert to both the pointer and assistive technology
 * while the dialog is up.
 *
 * Not the native `<dialog>` element. `showModal()` is imperative, its top layer
 * is not reachable from the tokens this product styles with, and Safari still
 * differs on scroll containment behind it. The behaviour below is the part that
 * matters and is the same in every browser we support.
 */
const PANEL =
  "flex h-dvh min-h-0 w-screen min-w-0 flex-col bg-card shadow-2xl sm:m-auto sm:h-auto " +
  "sm:max-h-[calc(100dvh-3rem)] sm:max-w-[calc(100vw-3rem)] sm:rounded-lg sm:border sm:border-border";

const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), " +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  title,
  closeLabel,
  onClose,
  restoreFocus,
  panelClassName,
  bodyClassName,
  children,
}: {
  /** Names the dialog. Rendered as its heading and used as its accessible name. */
  title: string;
  closeLabel: string;
  onClose: () => void;
  /**
   * The control to return focus to, resolved at close time rather than at open
   * time. A caller whose opener is re-created while the dialog is up, as the
   * procedure table openers are, needs to look it up again to hand focus back
   * to a button that is still on the page.
   */
  restoreFocus?: () => HTMLElement | null;
  panelClassName?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const restoreFocusRef = useRef(restoreFocus);

  useEffect(() => {
    onCloseRef.current = onClose;
    restoreFocusRef.current = restoreFocus;
  });

  useLayoutEffect(() => {
    // The page behind the dialog, which stops answering the pointer and the
    // accessibility tree while it is up. The dialog itself is portalled to the
    // body, so it is never inside what is made inert.
    const background =
      document.getElementById("root") ?? document.querySelector<HTMLElement>("main");
    const opener = document.activeElement;
    const previousAriaHidden = background?.getAttribute("aria-hidden") ?? null;
    const previousInert = background?.inert ?? false;
    const previousBodyStyles = {
      left: document.body.style.left,
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    const previousDocumentOverflow = document.documentElement.style.overflow;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    background?.setAttribute("aria-hidden", "true");
    if (background) background.inert = true;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = `-${scrollX}px`;
    document.body.style.width = "100%";
    closeButtonRef.current?.focus();

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const current = dialogRef.current;
      if (!current) return;
      const focusable = Array.from(current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (
        event.shiftKey &&
        (document.activeElement === first || !current.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !current.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      window.removeEventListener("keydown", handleKey);
      if (background) {
        background.inert = previousInert;
        if (previousAriaHidden === null) background.removeAttribute("aria-hidden");
        else background.setAttribute("aria-hidden", previousAriaHidden);
      }
      document.documentElement.style.overflow = previousDocumentOverflow;
      Object.assign(document.body.style, previousBodyStyles);
      if (scrollX !== 0 || scrollY !== 0) window.scrollTo(scrollX, scrollY);
      // After the paint that removes the dialog. The opener may be a button the
      // page is about to re-render, and focusing the detached one loses the
      // caret to the document body.
      window.setTimeout(() => {
        const target = restoreFocusRef.current?.() ?? opener;
        if (target instanceof HTMLElement && target.isConnected) target.focus();
      }, 0);
    };
  }, []);

  return createPortal(
    <div
      aria-labelledby={titleId}
      aria-modal="true"
      className="fixed inset-0 z-50 flex bg-foreground/60 sm:p-6"
      ref={dialogRef}
      role="dialog"
    >
      <div className={`${PANEL} ${panelClassName ?? ""}`}>
        <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
          <h2 className="font-semibold text-foreground" id={titleId}>
            {title}
          </h2>
          <button className="btn btn-small" onClick={onClose} ref={closeButtonRef} type="button">
            {closeLabel}
          </button>
        </div>
        <div className={bodyClassName ?? "min-h-0 flex-1 overflow-auto p-4 sm:p-5"}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
